import {AfterViewInit, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ECAggregateCommunity, ECAggregateEnergies, ECAggregateTotals} from 'smag-commons/dto/eco';
import {SmagApiService} from '../../api/smag-api.service';
import {ECMember, EnergyCommunity, EntitySubType, EntityType, SpatialCoordinates} from 'smag-commons/cfg/hierarchy';
import {catchError, concatMap, debounceTime, first, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, Subject, timer} from 'rxjs';
import {Hierarchy, HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {ConfigurationService} from '../../shared/configuration.service';
import {SharedEnergyData} from '../../shared/shared-energy-chart/shared-energy-chart.component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {ResizeService} from '../../shared/resize.service';
import {I18NService} from '../../i18n/i18n.service';
import {AuthService} from 'auth-commons';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {EconomicsError} from 'smag-commons/dto/eco/errors';
import {MsgBoxService} from '../../shared/msg-box.service';
import {Router} from '@angular/router';
import {isDefined, safeRatio} from '../../commons/commons';
import {TIME_DELTA_HOUR} from '../../shared/line-chart/line-chart.pipe';
import {DialogService} from '../../shared/dialog/dialog.service';
import {can} from '../../shared/permissions';
import {Permission, Scope} from 'smag-commons/dto/api';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {NewCommunityRequest} from '../ec-creator/ec-creator.component';
import * as _ from 'lodash';
import {EnergyCommunityNetwork} from 'smag-commons/src/dto/api';

enum Range {
    LAST_HOUR = 'Last hour',
    LAST_24_HOURS = 'Last 24 hours',
    LAST_7_DAYS = 'Last 7 days',
    LAST_30_DAYS = 'Last 30 days',
    LAST_60_DAYS = 'Last 60 days'
}

interface SharedData extends SharedEnergyData {
    // accountedSharedEnergy: number; // >=  0
}

export interface Community extends ECAggregateCommunity {
    name: string;
    type: string;
    active: boolean;
    resource: HierarchyEntity;
    client: string;
}

export interface Communities {
    communities: Community[];
    total: number;
    errors?: EconomicsError[];
}

export interface TenantInfo {
    id: string; // `null` means "true" root
    name: string;
    children: HierarchyEntity[];
}

enum SortCriterion {
    NAME = 'name',
    ACTIVATION_DATE = 'activation_date',
}

enum SortDirection {
    ASC = 'asc',
    DESC = 'desc'
}

const DEFAULT_SORT_CRITERIA: [SortCriterion, SortDirection][] = [
    [SortCriterion.NAME, SortDirection.ASC],
    [SortCriterion.ACTIVATION_DATE, SortDirection.DESC],
];


interface TypeFilter {
    rec: boolean;
    csg: boolean;
}

export interface ECNetworkSpatialCoordinates {
    name: string;
    spatial_coordinates: SpatialCoordinates;
}

interface ExpandedECAggregateEnergies extends ECAggregateEnergies {
    self_consumed_energy_ratio: number;
    shared_energy_ratio: number;
}

interface MemberSuggestion {
    firstName: string;
    lastName: string;
    shortName: string;
    pod: string;
    email: string;
    community: string;
    label: string;
    networkId: string;
}


const Ranges = [Range.LAST_HOUR, Range.LAST_24_HOURS, Range.LAST_7_DAYS, Range.LAST_30_DAYS, Range.LAST_60_DAYS];
const PAGE_SIZE = 15;
const PAGE_SIZES = [15, 30, 50];
const SEARCH_DEBOUNCE_MILLIS = 250;
const DEFAULT_TYPE_FILTER = {rec: true, csg: true};

@Component({
    selector: 'app-login',
    templateUrl: './dev-dashboard.component.html',
    styleUrls: ['./dev-dashboard.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Communities per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class DevDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadCommunities: Subject<void>;
    @ViewChild('paginator') private paginator: MatPaginator;
    @ViewChild('createCommunity') private communityCreatorDialog: TemplateRef<any>;

    contentResize: Observable<void>;
    canCreateCommunity: boolean;
    ecAggregateTotals: ECAggregateTotals;
    ecAggregateEnergies: ECAggregateEnergies;
    expandedEcAggregatedEnergies: ExpandedECAggregateEnergies;
    selectedRangeSubj: BehaviorSubject<Range>;
    searchSubj: BehaviorSubject<string>;
    nowSubj: BehaviorSubject<Moment>;
    sharedEnergy: SharedData[];
    displayedColumns: string[];
    readonly allColumns: string[];
    communities: BehaviorSubject<Communities>;
    newEntitySubj: BehaviorSubject<void>;
    searchText: string;
    showPremembershipLink: boolean;
    showRepresentativeBtn: boolean;
    premembershipBackofficeUrl: string;
    timeZone: string;

    memberSearchText = '';
    suggestedMembers: MemberSuggestion[] = [];
    selectedMember: MemberSuggestion = null;
    memberFilterSubject: Subject<any>;

    locationsSubj: BehaviorSubject<ECNetworkSpatialCoordinates[]>;
    Ranges = Ranges;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    TIME_DELTA_HOUR = TIME_DELTA_HOUR;
    isDefined = isDefined;

    form: FormGroup;
    tenants: TenantInfo[];

    SortCriterion = SortCriterion;
    SortDirection = SortDirection;
    sortCriteria: [SortCriterion, SortDirection][];
    sortSubject: BehaviorSubject<any>;

    typeFilter: TypeFilter;
    filterSubject: Subject<any>;
    tenantFilterSubject: Subject<any>;

    selectedRange: Range;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService,
                private readonly configurationService: ConfigurationService,
                private readonly resizeService: ResizeService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                readonly auth: AuthService,
                private readonly router: Router,
                private readonly dialogService: DialogService,
                private readonly fb: FormBuilder) {
        this.canCreateCommunity = false;
        this.ecAggregateTotals = null;
        this.ecAggregateEnergies = null;
        this.showPremembershipLink = false;
        this.showRepresentativeBtn = false;
        this.premembershipBackofficeUrl = null;

        this.selectedRange = Range.LAST_24_HOURS;
        this.selectedRangeSubj = new BehaviorSubject<Range>(this.selectedRange);
        this.nowSubj = new BehaviorSubject<Moment>(null);
        this.communities = new BehaviorSubject<Communities>(null);
        this.reloadCommunities = new Subject<void>();
        this.sharedEnergy = null;
        this.contentResize = this.resizeService.observe(['content']).pipe(takeUntil(this.destroyed));
        this.allColumns = ['name', 'type', 'activation_date', 'client', 'members', 'share', 'alarms'];
        this.displayedColumns = this.allColumns;
        this.newEntitySubj = new BehaviorSubject<void>(null);
        this.locationsSubj = new BehaviorSubject<ECNetworkSpatialCoordinates[]>(null);
        this.tenants = [];
        this.searchText = '';
        this.form = this.fb.group({
            selectedTenants: new FormControl(null, []),
            onlyInactive: new FormControl(false, [])
        });

        this.sortCriteria = [...DEFAULT_SORT_CRITERIA];
        this.typeFilter = DEFAULT_TYPE_FILTER;

        this.filterSubject = new BehaviorSubject(null);
        this.memberFilterSubject = new BehaviorSubject(null);
        // this subject remain because we need debounce events
        this.searchSubj = new BehaviorSubject<string>(null);
        this.searchSubj.pipe(debounceTime(SEARCH_DEBOUNCE_MILLIS), takeUntil(this.destroyed)).subscribe(this.filterSubject);

        this.sortSubject = new BehaviorSubject<any>(null);
        this.sortSubject.pipe(takeUntil(this.destroyed)).subscribe(this.filterSubject);

        this.tenantFilterSubject = new BehaviorSubject(null);
        this.form.controls.selectedTenants.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(this.tenantFilterSubject);
    }

    ngOnInit() {
        this.hierarchyService.hierarchy.pipe(
            takeUntil(this.destroyed)
        ).subscribe(h => {
            this.canCreateCommunity = canCreateCommunity(h);
            this.showRepresentativeBtn = this.auth.profile.scopes?.includes(Scope.EMS) &&
                (can(h.permissions, Permission.READ) || !!(h.entities || [])
                    .filter(r => r.type === EntityType.ROOT_NODE)
                    .find(r => can(r.permissions, Permission.READ)));
            this.loadPremembership();
        });

        merge(this.tenantFilterSubject, this.newEntitySubj).pipe(
            switchMap(() => {
                const tenantIds = (this.formFilter.selectedTenants.value || []).map(tenant => tenant.id);
                return this.mkEcAggregateTotals(tenantIds);
            })
        ).subscribe(response => {
            this.ecAggregateTotals = response;
        });

        combineLatest([this.selectedRangeSubj,
            this.configurationService.timeConfig,
            this.tenantFilterSubject,
            this.newEntitySubj]).pipe(
            takeUntil(this.destroyed)
        ).pipe(
            tap(() => this.ecAggregateEnergies = null),
            switchMap(([range, tc]) => {
                const timeZone = tc.time_zone;
                this.timeZone = tc.time_zone;
                this.nowSubj.next(moment.tz(timeZone).startOf('hour'));
                const tenantIds = (this.formFilter.selectedTenants.value || []).map(tenant => tenant.id);
                return this.mkEcAggregateEnergies(range, this.nowSubj.getValue(), tenantIds);
            })
        ).subscribe(response => {
            this.ecAggregateEnergies = response;
            this.expandedEcAggregatedEnergies = this.mkExpandedEcAggregatedEnergies();
            this.sharedEnergy = this.mkSharedEnergy(response);
        });

        this.hierarchyService.hierarchy.pipe(
            takeUntil(this.destroyed)
        ).subscribe(hierarchy => {
            const tenants = hierarchy.entities.filter(res =>
                res.type === EntityType.ROOT_NODE && can(res.permissions, Permission.READ));
            this.tenants = [];
            if (can(hierarchy.permissions, Permission.READ)) {
                this.tenants.push({
                    id: hierarchy.root,
                    name: this.i18n.translate('None__{root}'),
                    children: hierarchy.entities
                });
            }
            tenants.forEach(res => this.tenants.push({id: res.id, name: res.name, children: res.children}));
            if (this.tenants.length) {
                this.formFilter.selectedTenants.setValue(this.tenants);
            }
            if (this.tenants.length <= 1) {
                this.displayedColumns = this.allColumns.filter(c => c !== 'client');
            } else {
                this.displayedColumns = this.allColumns;
            }
            this.tenantFilterSubject.pipe(debounceTime(SEARCH_DEBOUNCE_MILLIS), takeUntil(this.destroyed))
                // map RootNodeInfo[] => string[]
                // map(value => (this.form.controls.selectedTenants.value as RootNodeInfo[]).map(rni => rni.id)))
                .subscribe(this.filterSubject);
        });

        this.onlyInactiveFormControl.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            this.filterSubject.next(null);
        });

    }

    ngAfterViewInit() {
        merge(this.paginator.page,
            this.reloadCommunities.pipe(tap(() => this.paginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() => combineLatest([
                this.filterSubject,
                this.memberFilterSubject,
                this.newEntitySubj])),
            switchMap(() => {
                    const tenantIds = this.formFilter.selectedTenants.value?.map(tenant => tenant.id);
                    const sortCriterion = mkSortParams(this.sortCriteria);
                    const name = this.searchText;
                    const types = mkSubType(this.typeFilter);
                    // if only active is true, inactive=false, otherwise filter is disabled (inactive = null)
                    const onlyInactive = (this.formFilter.onlyInactive.value as boolean) ? true : null;
                    // Hardcoded start of year for communities statistics
                    return this.mkCommunities(moment.tz(this.timeZone).startOf('year').valueOf(),
                        moment.tz(this.timeZone).startOf('hour').valueOf(),
                        this.paginator.pageIndex + 1,
                        this.paginator.pageSize,
                        name,
                        tenantIds,
                        types,
                        sortCriterion,
                        onlyInactive).pipe(
                        startWith(null as Communities), // triggers spinner
                        catchError(err => {
                            this.msgBoxService.error(this.i18n.translate('An error occurred while retrieving communities'), err);
                            return of(null as Communities);
                        }));
                }
            ),
            takeUntil(this.destroyed)
        ).subscribe((communities) => {
            if (communities) {
                this.getLocations(communities);
                this.communities.next(communities);
            }
        });
    }


    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onSelectRange(r: Range) {
        this.selectedRange = r;
        this.selectedRangeSubj.next(r);
    }

    onClickCreateEnergyCommunity() {
        this.dialogService.open<any, EnergyCommunity>({
            title: this.i18n.translate('Create community'),
            template: this.communityCreatorDialog,
            context: {}
        });
    }

    onClickGoToPremembership() {
        window.location.href = this.premembershipBackofficeUrl;
    }

    onClickGoToCommunity(community: Community) {
        this.gotoCommunityDashboard(community.id).then();
    }

    onClickClearSearchText() {
        this.searchText = '';
        this.paginator.firstPage();
        this.searchSubj.next(this.searchText);
    }

    onClickClearMemberSearchText() {
        this.memberSearchText = '';
        if (this.selectedMember) {
            this.selectedMember = null;
            this.paginator.firstPage();
            this.memberFilterSubject.next();
        }
    }

    onClickSearchText() {
        const search = this.cleanSearchText;
        if (search) {
            this.paginator.firstPage();
        }
        this.searchSubj.next(search);
    }

    onChangeMemberSearchText() {
        if (this.memberSearchText && this.memberSearchText.length > 4) {
            timer(250).pipe(
                switchMap(() => combineLatest([
                    this.apiService.searchMembers(1000, true, this.memberSearchText, this.memberSearchText, this.memberSearchText,
                        this.memberSearchText, this.memberSearchText),
                    this.apiService.getNetworks()
                        .pipe(map(r => r.results.map(rr => rr as EnergyCommunityNetwork)))
                ])),
                map(([members, communities]) => _.flatten(members.map(member => mkMemberSuggestions(member, communities)))
                    .sort((memberOne, memberIno) => memberOne.pod.localeCompare(memberIno.pod)))
            ).subscribe(members => this.suggestedMembers = members);
        } else {
            this.suggestedMembers = [];
        }
    }

    onOptionSelected(member: MemberSuggestion) {
        this.paginator.firstPage();
        this.selectedMember = member;
        this.memberSearchText = member?.label ?? '';
        this.memberFilterSubject.next();
    }

    onCreateCommunity(req: NewCommunityRequest, closeFn: () => void) {
        if (req) {
            this.createCommunity(req, closeFn)
                .then(communityId => this.gotoCommunityDashboard(communityId));
        } else {
            closeFn();
        }
    }

    goToRepresentativesTable() {
        this.router.navigate(['/c', 'ec', 'representatives'], {
            queryParamsHandling: 'merge'
        }).then();
    }

    goToMembers() {
        this.router.navigate(['/c', 'ec', 'members'],
            {
                queryParams: {id: this.selectedMember.networkId},
                state: {pod: this.selectedMember.pod},
                queryParamsHandling: 'merge'
            }).then();
    }

    getRatioFor(val1, val2): number {
        const ratio = safeRatio(val1, val2);
        return isDefined(ratio) ? ratio * 100 : null;
    }

    mkExpandedEcAggregatedEnergies(): ExpandedECAggregateEnergies {
        return {
            produced_energy: this.ecAggregateEnergies.produced_energy,
            effective_injected_energy: this.ecAggregateEnergies.effective_injected_energy,
            consumed_energy: this.ecAggregateEnergies.consumed_energy,
            absorbed_energy: this.ecAggregateEnergies.absorbed_energy,
            self_consumed_energy: this.ecAggregateEnergies.self_consumed_energy,
            shared_energy: this.ecAggregateEnergies.shared_energy,
            incentive_shared_energy: this.ecAggregateEnergies.incentive_shared_energy,
            saved_co2: this.ecAggregateEnergies.saved_co2,
            saved_gas: this.ecAggregateEnergies.saved_gas,
            times: this.ecAggregateEnergies.times,
            hourly_absorbed_energy: this.ecAggregateEnergies.hourly_absorbed_energy,
            hourly_effective_injected_energy: this.ecAggregateEnergies.hourly_effective_injected_energy,
            hourly_shared_energy: this.ecAggregateEnergies.hourly_shared_energy,
            hourly_accounted_shared_energy: this.ecAggregateEnergies.hourly_accounted_shared_energy,
            errors: [],
            self_consumed_energy_ratio: this.getRatioFor(this.ecAggregateEnergies.self_consumed_energy, this.ecAggregateEnergies.produced_energy),
            shared_energy_ratio: this.getRatioFor(this.ecAggregateEnergies.shared_energy, this.ecAggregateEnergies.effective_injected_energy),
        };
    }

    get cleanSearchText() {
        return this.searchText.trim();
    }

    get injectedColor(): string {
        return this.cfg.shared.colors.effectiveInjectedEnergy;
    }

    get absorbedColor(): string {
        return this.cfg.shared.colors.absorbedPower;
    }

    get sharedColor(): string {
        return this.cfg.shared.colors.sharedPower;
    }

    get gridColor(): string {
        return this.cfg.shared.gridColor;
    }

    get tooltipBorderColor(): string {
        return this.cfg.shared.tooltipBorderColor;
    }

    get tooltipBackgroundColor(): string {
        return this.cfg.shared.tooltipBackgroundColor;
    }

    get selectionLineColor(): string {
        return this.cfg.shared.selectionLineColor;
    }

    get formFilter(): { [ctrl: string]: FormControl } {
        return this.form.controls as { [ctrl: string]: FormControl };
    }

    get onlyInactiveFormControl(): FormControl {
        return this.form.controls.onlyInactive as FormControl;
    }

    get onlyAlarmsFormControl(): FormControl {
        return this.form.controls.onlyAlarms as FormControl;
    }


    hasAlarms(row: Community) {
        return row.inactive_plants > 0 || row.inactive_pods > 0 || row.missing_profiles > 0;
    }


    canFilterTenant(tenant: TenantInfo): boolean {
        const selectedTenant = this.formFilter.selectedTenants.value as TenantInfo[];
        return !selectedTenant.includes(tenant) || selectedTenant.length > 1;
    }

    private getLocations(communities: Communities) {
        if (communities.total === 0) {
            this.locationsSubj.next([]); // give an empty SpatialCoordinates array to google map component to remove markers from map
        }
        forkJoin(communities.communities.map(community => this.apiService.getEntity(community.id)))
            .subscribe(entities => {
                const values = entities.map((entity, index) => {
                    const ec: ECNetworkSpatialCoordinates = {
                        spatial_coordinates: entity.spatial_coordinates,
                        name: communities.communities[index].name
                    };
                    return ec;
                }).filter(x => x.spatial_coordinates);
                this.locationsSubj.next(values);
            });
    }

    private createCommunity(req: NewCommunityRequest, closeFn: () => void): Promise<string> {
        return this.apiService.createEnergyCommunity(req.community)
            .then((idResult) => {
                if (req.representative) {
                    return this.addRepresentative(req.representative.id, idResult.id)
                        .then(() => idResult.id);
                } else {
                    return Promise.resolve(idResult.id);
                }
            })
            .then(id => {
                closeFn();
                return id;
            })
            .then(id => {
                this.msgBoxService.info(this.i18n.translate('Energy Community {0} successfully added', name));
                return id;
            })
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot add resource {0}', name), err);
                throw err;
            })
            .then(id => this.hierarchyService.reloadHierarchy().then(() => id));
    }

    private addRepresentative(representativeId: string, networkId: string): Promise<void> {
        return this.apiService.addNetworkToRepresentative(representativeId, networkId).then();
    }

    private mkCommunities(fromTime: number, toTime: number,
                          page: number, size: number, name: string,
                          tenantIds: string[], types: EntitySubType[],
                          sortCriterion: string[],
                          inactive: boolean): Observable<Communities> {
        // quickly load aggregate information without economics, so that the table is readily available;
        // then, the same request is sent also requiring "economics" data
        return of(false, true).pipe(
            concatMap(economics => forkJoin([
                this.apiService.ecAggregateEconomicsCommunities('rose',
                    page, size,
                    fromTime, toTime,
                    tenantIds,
                    this.selectedMember?.networkId ? [this.selectedMember?.networkId] : [],
                    name,
                    inactive,
                    types,
                    sortCriterion,
                    economics),
                this.hierarchyService.hierarchyIds.pipe(first())
            ]))
        ).pipe(
            map(([communities, entities]) => communities.communities.length ?
                {
                    communities: communities.communities.map(c => this.mkCommunity(c, entities, toTime)),
                    errors: communities.errors,
                    total: communities.total
                } as Communities :
                {
                    communities: [],
                    total: 0
                } as Communities)
        );
    }

    private mkCommunity(ecCommunity: ECAggregateCommunity,
                        entities: { [entityId: string]: HierarchyEntity },
                        toTime: number): Community {
        return {
            ...ecCommunity,
            client: entities[ecCommunity.id].parent ? entities[ecCommunity.id].parent.name : '',
            name: entities[ecCommunity.id].name,
            type: mkCommunityType(entities[ecCommunity.id]),
            active: ecCommunity.activation_date < toTime,
            resource: entities[ecCommunity.id]
        } as Community;
    }

    private mkEcAggregateTotals(tenantIds: string[]): Observable<ECAggregateTotals> {
        this.ecAggregateTotals = null;
        return this.apiService.ecAggregateEconomics('rose',
            tenantIds);
    }

    private mkEcAggregateEnergies(selectedRange: Range, now: Moment, tenantIds: string[]): Observable<ECAggregateEnergies> {
        return this.apiService.ecAggregateEconomicsEnergies('rose',
            this.getFrom(now, selectedRange).valueOf(), now.valueOf(),
            tenantIds,
            undefined,
            undefined, // NOTE: do not filter inactive communities
            undefined);
    }

    private mkSharedEnergy(ecAggregateEnergies: ECAggregateEnergies): SharedData[] {
        return ecAggregateEnergies.times.map((t, i, tt) => ({
            time: t,
            absorbedEnergy: ecAggregateEnergies.hourly_absorbed_energy[i],
            injectedEnergy: ecAggregateEnergies.hourly_effective_injected_energy[i],
            sharedEnergy: ecAggregateEnergies.hourly_shared_energy[i],
            opacity: 1,
            strokeWidth: 3
        }));
    }

    private getFrom(now: Moment, selectedRange: Range): Moment {
        if (selectedRange === Range.LAST_HOUR) {
            return now.clone().subtract(1, 'hour');
        } else if (selectedRange === Range.LAST_24_HOURS) {
            return now.clone().subtract(1, 'day');
        } else if (selectedRange === Range.LAST_7_DAYS) {
            return now.clone().subtract(7, 'days');
        } else if (selectedRange === Range.LAST_30_DAYS) {
            return now.clone().subtract(30, 'days');
        } else if (selectedRange === Range.LAST_60_DAYS) {
            return now.clone().subtract(60, 'days');
        }
    }

    private gotoCommunityDashboard(communityId: string): Promise<void> {
        return this.router.navigate(['/c', 'ec', 'dashboard'],
            {
                queryParams: {
                    id: communityId
                },
                queryParamsHandling: 'merge'
            }).then();
    }

    private loadPremembership() {
        this.apiService.getCustomerConfig()
            .subscribe(customerCfg => {
                this.showPremembershipLink = this.auth.profile.scopes?.includes(Scope.EMS) &&
                    !!customerCfg.premembership_backoffice_url;
                this.premembershipBackofficeUrl = customerCfg.premembership_backoffice_url;
            });
    }

    resetFilters() {
        this.searchText = '';
        this.memberSearchText = '';
        this.form.controls.selectedTenants.setValue(this.tenants);
        this.form.controls.onlyInactive.setValue(false);
        this.typeFilter.csg = true;
        this.typeFilter.rec = true;
        this.sortCriteria = DEFAULT_SORT_CRITERIA;
        this.selectedRange = (Range.LAST_24_HOURS);
        this.filterSubject.next(null);
        this.memberFilterSubject.next();
    }

    getSortDirection(criterion: SortCriterion): SortDirection {
        const sortCriterion = this.sortCriteria.find(c => c[0] === criterion);
        return sortCriterion ? sortCriterion[1] : null;
    }

    onClickSortBy(criterion: SortCriterion, direction: SortDirection) {
        // remove current matching criterion (regardless of its direction)
        // and add new criterion right on top of criteria list
        this.sortCriteria = [
            [criterion, direction],
            ...this.sortCriteria.filter(c => c[0] !== criterion)
        ];
        // reload members
        this.sortSubject.next(null);
    }

    onClickType() {
        this.filterSubject.next(null);
    }
}

function mkCommunityType(res: HierarchyEntity): string {
    switch (res.sub_type) {
        case EntitySubType.RENEWABLE_ENERGY_COMMUNITY:
            return 'REC';
        case EntitySubType.COLLECTIVE_SELF_CONSUMPTION_GROUP:
            return 'CSG';
        default:
            return res.sub_type;
    }
}

function canCreateCommunity(hierarchy: Hierarchy): boolean {
    if (can(hierarchy.permissions, Permission.CREATE)) {
        // at least we can create communities under root
        return true;
    }
    // check if we can create communities under at least one root node
    return !!hierarchy.entities.filter(res =>
        res.type === EntityType.ROOT_NODE && can(res.permissions, Permission.CREATE))
        .length;
}

function mkSubType(f: TypeFilter): EntitySubType[] {
    let ret: EntitySubType[] = new Array<EntitySubType>();
    if (f.rec) {
        ret = ret.concat([EntitySubType.RENEWABLE_ENERGY_COMMUNITY]);
    }
    if (f.csg) {
        ret = ret.concat([EntitySubType.COLLECTIVE_SELF_CONSUMPTION_GROUP]);
    }
    return ret;
}

function mkSortParams(criteria: [SortCriterion, SortDirection][]): string[] {
    return criteria.map(([c, s]) => c.toString() + '_' + s.toString());
}

function mkMemberSuggestions(member: ECMember, communities: EnergyCommunityNetwork[]): MemberSuggestion[] {
    const firstName = member.personal_data.first_name;
    const lastName = member.personal_data.last_name;
    const shortName = `${firstName.substring(0, 3)} ${lastName.substring(0, 3)}`;
    const email = member.personal_data.email;
    const networkId = member.network_id;
    const community = getCommunityNameFromCommunityId(networkId, communities);
    return member.pods.map(pod => ({
        firstName,
        lastName,
        shortName,
        pod: pod.pod,
        email,
        community,
        label: `${firstName} ${lastName}, ${pod.pod}, ${email ? `${email},` : ''} ${community}`,
        networkId
    }));
}

function getCommunityNameFromCommunityId(id: string, communities: EnergyCommunityNetwork[]): string {
    return communities.find(community => community.network_id === id)?.name ?? '';
}
